'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { HelpCircle, Search, Loader2, CheckCircle, ThumbsUp, ThumbsDown, ChevronRight, BookOpen, Video, Bot, Send, Clock } from 'lucide-react';


interface KBArticle {
  id: string;
  title: string;
  slug: string;
  category: string;
  tags: string[];
  content: string;
  helpful: number;
  notHelpful: number;
  views: number;
  createdAt: string;
  updatedAt: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

const CATEGORIES = ['Tutti', 'Chiamate', 'SMS', 'Pazienti', 'Fatturazione', 'Integrazioni', 'Sicurezza', 'Account'];

export default function HelpPage() {
  const [articles, setArticles] = useState<KBArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Tutti');
  const [activeTab, setActiveTab] = useState<'kb' | 'chat' | 'videos'>('kb');
  const [selectedArticle, setSelectedArticle] = useState<KBArticle | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    { role: 'assistant', content: 'Ciao! Sono l\'assistente AI di LabCallManager. Come posso aiutarti?', timestamp: new Date().toISOString() },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (category !== 'Tutti') params.set('category', category);
      const res = await fetch(`/api/help/kb?${params}`);
      const json = await res.json();
      setArticles(json.data ?? []);
    } catch { setArticles([]); } finally { setLoading(false); }
  }, [search, category]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const handleFeedback = async (id: string, helpful: boolean) => {
    try {
      await fetch(`/api/help/kb/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ helpful }),
      });
      setArticles(prev => prev.map(a => a.id === id ? {
        ...a,
        helpful: helpful ? a.helpful + 1 : a.helpful,
        notHelpful: !helpful ? a.notHelpful + 1 : a.notHelpful,
      } : a));
      showToast('Grazie per il feedback!');
    } catch {}
  };

  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    const userMsg: ChatMessage = { role: 'user', content: chatInput, timestamp: new Date().toISOString() };
    setChatMessages(prev => [...prev, userMsg]);
    const question = chatInput;
    setChatInput('');
    setChatLoading(true);
    try {
      const res = await fetch('/api/ai/call-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: question, context: 'help_faq', mode: 'faq' }),
      });
      const json = await res.json();
      const answer = json.summary ?? json.answer ?? 'Mi dispiace, non ho trovato una risposta specifica. Prova a cercare nella knowledge base o contatta il supporto.';
      setChatMessages(prev => [...prev, { role: 'assistant', content: answer, timestamp: new Date().toISOString() }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Si è verificato un errore. Riprova o contatta il supporto.', timestamp: new Date().toISOString() }]);
    } finally { setChatLoading(false); }
  };

  const VIDEO_TUTORIALS = [
    { id: '1', title: 'Introduzione a LabCallManager', duration: '5:30', category: 'Onboarding', thumbnail: '🎬' },
    { id: '2', title: 'Gestire le chiamate in arrivo', duration: '3:45', category: 'Chiamate', thumbnail: '📞' },
    { id: '3', title: 'Configurare i template SMS', duration: '4:20', category: 'SMS', thumbnail: '💬' },
    { id: '4', title: 'Importare pazienti da CSV', duration: '6:10', category: 'Pazienti', thumbnail: '👥' },
    { id: '5', title: 'Creare campagne outbound', duration: '7:00', category: 'Campagne', thumbnail: '📊' },
    { id: '6', title: 'Configurare il calendario', duration: '4:50', category: 'Calendario', thumbnail: '📅' },
  ];

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {toast && (
          <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-green-600 text-white px-4 py-3 rounded-xl shadow-lg text-sm font-medium">
            <CheckCircle size={16} />{toast}
          </div>
        )}

        {/* Header */}
        <div className="bg-white border-b border-border px-6 lg:px-8 py-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <HelpCircle size={17} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Help Center</h1>
              <p className="text-xs text-muted-foreground">Knowledge base, video tutorial e assistente AI</p>
            </div>
          </div>
        </div>

        <div className="px-6 lg:px-8 py-6 max-w-5xl mx-auto">
          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-muted p-1 rounded-lg w-fit">
            {[{ id: 'kb', label: '📚 Knowledge Base' }, { id: 'chat', label: '🤖 Assistente AI' }, { id: 'videos', label: '🎬 Video Tutorial' }].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id as typeof activeTab)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === t.id ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'kb' && (
            <div className="space-y-5">
              {/* Search */}
              <div className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Cerca nella knowledge base..."
                  className="w-full pl-11 pr-4 py-3 text-sm border border-border rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white shadow-sm"
                />
              </div>

              {/* Categories */}
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map(cat => (
                  <button key={cat} onClick={() => setCategory(cat)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${category === cat ? 'bg-primary text-white' : 'bg-white border border-border text-muted-foreground hover:bg-muted'}`}>
                    {cat}
                  </button>
                ))}
              </div>

              {selectedArticle ? (
                <div className="bg-white rounded-2xl border border-border p-6">
                  <button onClick={() => setSelectedArticle(null)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4">
                    <ChevronRight size={12} className="rotate-180" /> Torna alla lista
                  </button>
                  <h2 className="text-xl font-bold text-foreground mb-2">{selectedArticle.title}</h2>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                    <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full">{selectedArticle.category}</span>
                    <span className="flex items-center gap-1"><Clock size={11} />{new Date(selectedArticle.updatedAt).toLocaleDateString('it-IT')}</span>
                    <span>{selectedArticle.views} visualizzazioni</span>
                  </div>
                  <div className="prose prose-sm max-w-none text-foreground">
                    <p className="whitespace-pre-wrap">{selectedArticle.content}</p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-border flex items-center gap-3">
                    <p className="text-xs text-muted-foreground">Questo articolo è stato utile?</p>
                    <button onClick={() => handleFeedback(selectedArticle.id, true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs hover:bg-green-100">
                      <ThumbsUp size={12} /> Sì ({selectedArticle.helpful})
                    </button>
                    <button onClick={() => handleFeedback(selectedArticle.id, false)} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs hover:bg-red-100">
                      <ThumbsDown size={12} /> No ({selectedArticle.notHelpful})
                    </button>
                  </div>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-primary" /></div>
              ) : articles.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-border">
                  <BookOpen size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                  <p className="font-semibold text-foreground">Nessun articolo trovato</p>
                  <p className="text-sm text-muted-foreground mt-1">Prova con termini di ricerca diversi</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {articles.map(article => (
                    <button key={article.id} onClick={() => setSelectedArticle(article)} className="bg-white rounded-2xl border border-border p-4 text-left hover:shadow-sm transition-shadow group">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full font-medium">{article.category}</span>
                        <ChevronRight size={14} className="text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">{article.title}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-2">{article.content.slice(0, 120)}...</p>
                      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><ThumbsUp size={10} />{article.helpful}</span>
                        <span>{article.views} visualizzazioni</span>
                        <div className="flex gap-1 ml-auto">
                          {article.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="px-1.5 py-0.5 bg-muted rounded text-[10px]">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="bg-white rounded-2xl border border-border flex flex-col" style={{ height: '600px' }}>
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Assistente AI</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500" />Online</p>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {chatMessages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-primary text-white rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm'}`}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-muted px-4 py-3 rounded-2xl rounded-bl-sm">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-border flex gap-2">
                <input
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleChatSend()}
                  placeholder="Fai una domanda..."
                  className="flex-1 px-4 py-2.5 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <button onClick={handleChatSend} disabled={chatLoading || !chatInput.trim()} className="px-4 py-2.5 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors">
                  <Send size={16} />
                </button>
              </div>
            </div>
          )}

          {activeTab === 'videos' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {VIDEO_TUTORIALS.map(video => (
                <div key={video.id} className="bg-white rounded-2xl border border-border overflow-hidden hover:shadow-sm transition-shadow cursor-pointer group">
                  <div className="aspect-video bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                    <div className="text-center">
                      <span className="text-4xl">{video.thumbnail}</span>
                      <div className="mt-2 w-10 h-10 rounded-full bg-primary/80 flex items-center justify-center mx-auto group-hover:bg-primary transition-colors">
                        <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-white border-b-[6px] border-b-transparent ml-1" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">{video.category}</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={10} />{video.duration}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mt-2">{video.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
